import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setAuthToken, clearAuthToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // PWA install analytics: when an authed user lands while running in standalone
  // mode (i.e. they installed the app), ping /me/installed once. Idempotent server-side;
  // localStorage key keeps us from re-pinging within the same install.
  // Also listens for `appinstalled` (fires when the user clicks Install in Chrome/Edge)
  // so we capture the moment of conversion in the same tab.
  useEffect(() => {
    if (!user || typeof window === "undefined") return;
    const key = `gwg_installed_pinged_${user.user_id}`;
    const ping = () => {
      if (window.localStorage.getItem(key) === "1") return;
      api.post("/me/installed")
        .then(() => window.localStorage.setItem(key, "1"))
        .catch(() => {});
    };
    const isStandalone =
      window.navigator.standalone === true ||
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      (window.matchMedia && window.matchMedia("(display-mode: minimal-ui)").matches);
    if (isStandalone) ping();
    const onInstalled = () => ping();
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, [user]);

  const login = async (email, password, remember_me = false) => {
    const { data } = await api.post("/auth/login", { email, password, remember_me });
    if (data?.token) setAuthToken(data.token);
    setUser(data);
    return data;
  };
  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    if (data?.token) setAuthToken(data.token);
    setUser(data);
    return data;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    clearAuthToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
